import { useTranslation } from 'react-i18next';
import { AnimatedDashboard } from '../../components/animation/AnimatedDashboard';
import { colors } from '../../styles/GlobalStyles';
import { H3, P2 } from '../../styles/FontStyles';
import { PageTitle } from '../../styles/GlobalComponents';
import {
  ProductSlider,
  ResourceLayout,
  ResourceGrid,
  ProductCard,
  ProductBanner,
  ProductBody,
  ResourceAction,
  ActionIcon,
  PrimaryAction,
  ResourceSection,
  DocumentCard,
  DocumentHeading,
  DocumentBadge,
  DocumentDescription,
  LanguageList,
  SocialCard,
  SocialIcon,
  Benefits,
  StoreIcons
} from './ResourcesStyled';
import futuraImage from '../../assets/images/backoffice/resources/futuraMining.png';
import unicaImage from '../../assets/images/backoffice/resources/unicaPay.png';
import minaraImage from '../../assets/images/backoffice/resources/minara.png';
import academyImage from '../../assets/images/backoffice/resources/u1academy.png';
import facebookIcon from '../../assets/icons/facebook.png';
import instagramIcon from '../../assets/icons/instagram.png';
import appleIcon from '../../assets/icons/backOffice/apple.svg';
import { Desktop, Mobile } from '../../App';

import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// Add confirmed resource URLs here. Unavailable destinations remain disabled.
const resourceLinks: Record<string, string | undefined> = {
  futuraOnePager:
    'https://resources.unitedone.global/presentations/one-page-futura.png',
  futuraTutorials:
    'https://www.youtube.com/playlist?list=PLJbL2AZfXyTORBrg5-eIo2gA3TPKOr-G8',
  unicaApple:
    'https://apps.apple.com/us/app/unica-pay-finance-evolved/id6757447315',
  unicaAndroid:
    'https://play.google.com/store/apps/details?id=com.domainunica.appunica&hl=en_US',
  unicaTutorials:
    'https://www.youtube.com/playlist?list=PLJbL2AZfXyTMRPWhM2USuTIABvH4A-Hpf',
  minaraDocs: 'https://minara.ai/docs',
  minaraTutorials:
    'https://www.youtube.com/playlist?list=PLJbL2AZfXyTM6TXvMmhi9Uf6vHIJlFoUL',
  onePager: 'https://resources.unitedone.global/presentations/one-page.jpeg',
  translations:
    'https://drive.google.com/drive/folders/1YyRb7SumuEoVTGRmaAKOShstLYPt-y1w',
  youtube: 'https://www.youtube.com/@UnitedOne.Global',
  futuraWebsite: 'https://futura-mining.com/',
  unicaWebsite: 'https://www.unicapay.com/',
  minaraWebsite: 'https://minara.ai/app/chat',
  academyWebsite: 'https://lms.unitedone.global/login',
  compensationPlan:
    'https://resources.unitedone.global/presentations/comp-plan.pdf',
  facebook: 'https://www.facebook.com/UnitedOne.Global',
  instagram: 'https://www.instagram.com/unitedone.global',
  telegram: 'https://t.me/UnitedOne_Global'
};

const products = [
  {
    id: 'futura',
    name: 'FUTURA MINING',
    category: 'BITCOIN MINING',
    image: futuraImage,
    description: 'Fractions, hosting & how Futura works.',
    actions: [
      { label: 'One-pager', key: 'futuraOnePager', download: true },
      { label: 'Tutorials', key: 'futuraTutorials' }
    ]
  },
  {
    id: 'unica',
    name: 'UNICA PAY',
    category: 'PAYMENTS & CARDS',
    image: unicaImage,
    description: 'Cards, payments & how Unica Pay works.',
    actions: [{ label: 'Tutorials', key: 'unicaTutorials' }]
  },
  {
    id: 'minara',
    name: 'MINARA AI',
    category: 'AI ASSISTANT',
    image: minaraImage,
    description: 'Your 24/7 AI trading assistant — overview & setup.',
    actions: [
      { label: 'Docs & guides', key: 'minaraDocs' },
      { label: 'Tutorials', key: 'minaraTutorials' }
    ]
  },
  {
    id: 'academy',
    name: 'UONE ACADEMY',
    category: 'EDUCATION & TRAINING',
    image: academyImage,
    description: 'Courses & training to master the business & products.',
    actions: []
  }
];

const externalProps = (key: string) => ({
  href: resourceLinks[key],
  target: '_blank',
  rel: 'noopener noreferrer',
  'aria-disabled': !resourceLinks[key] || undefined,
  title: resourceLinks[key] ? undefined : 'Link coming soon'
});

const Arrow = () => <span aria-hidden='true'>↗</span>;
const Download = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    aria-hidden='true'>
    <path
      d='M7 18H6a4 4 0 0 1-.6-7.95 6.5 6.5 0 0 1 12.6-1.3A4.7 4.7 0 0 1 18 18h-1M12 12v9m-3-3 3 3 3-3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const DriveIcon = () => (
  <svg width='30' height='30' viewBox='0 0 30 30' aria-hidden='true'>
    <path fill='#00ac47' d='M10 2h10L9 21H0z' />
    <path fill='#ffba00' d='M20 2 30 21H20L10 2z' />
    <path fill='#2684fc' d='M0 21h30l-5 8H5z' />
  </svg>
);

const ProductResourceCard = ({
  product
}: {
  product: (typeof products)[number];
}) => (
  <ProductCard>
    <ProductBanner $image={product.image}>
      <span>{product.category}</span>
      <h4>{product.name}</h4>
    </ProductBanner>
    <ProductBody>
      <p>{product.description}</p>
      <div>
        {product.id === 'unica' && (
          <ResourceAction as='div'>
            <span>Get the app</span>
            <StoreIcons>
              <a
                {...externalProps('unicaApple')}
                aria-label='Download Unica Pay on the App Store'>
                <img src={appleIcon} alt='' />
              </a>
              <a
                {...externalProps('unicaAndroid')}
                aria-label='Download Unica Pay on Google Play'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  aria-hidden='true'>
                  <path fill='#00d1ff' d='M3 2v20l11-10z' />
                  <path fill='#00ee76' d='m3 2 14 8-3 2z' />
                  <path fill='#ffce00' d='m17 10 4 2-4 2-3-2z' />
                  <path fill='#ff4b64' d='m3 22 14-8-3-2z' />
                </svg>
              </a>
            </StoreIcons>
          </ResourceAction>
        )}
        {product.actions.map((action) => (
          <ResourceAction key={action.key} {...externalProps(action.key)}>
            {action.label}
            <ActionIcon>
              {'download' in action && action.download ? (
                <Download />
              ) : (
                <Arrow />
              )}
            </ActionIcon>
          </ResourceAction>
        ))}
        {product.id === 'academy' && (
          <Benefits>
            {[
              'Courses that start from zero',
              'Learn at your own pace, 24/7',
              'Certificates when you finish'
            ].map((text) => (
              <li key={text}>
                <span aria-hidden='true'>✓</span>
                {text}
              </li>
            ))}
          </Benefits>
        )}
      </div>
      <PrimaryAction {...externalProps(`${product.id}Website`)}>
        {product.id === 'academy' ? 'Explore courses' : 'Visit website'}
        <Arrow />
      </PrimaryAction>
    </ProductBody>
  </ProductCard>
);

export const Resources = () => {
  const { t } = useTranslation('resources');
  return (
    <AnimatedDashboard zoom dark key='resources'>
      <PageTitle mt='0' color={colors.primary900}>
        {t('resources')}
      </PageTitle>
      <H3 mt='0' color={colors.neutral500}>
        PRODUCT RESOURCES
      </H3>
      <P2 mb='20'>Everything to explain and promote each product.</P2>
      <ResourceLayout>
        <Desktop>
          <ResourceGrid>
            {products.map((product) => (
              <ProductResourceCard key={product.id} product={product} />
            ))}
          </ResourceGrid>
        </Desktop>
        <Mobile>
          <ProductSlider width='100%' margin='0'>
            <Swiper
              slidesPerView={1.12}
              modules={[Pagination]}
              pagination={{ clickable: true }}
              spaceBetween={16}
              loop={true}>
              {products.map((product) => (
                <SwiperSlide key={product.id}>
                  <ProductResourceCard product={product} />
                </SwiperSlide>
              ))}
            </Swiper>
          </ProductSlider>
        </Mobile>

        <ResourceSection>
          <H3 color={colors.neutral500}>DOCUMENTS &amp; PDFS</H3>
          <P2 mb='20'>
            Official documents ready to download. Translations live in the
            shared Drive library.
          </P2>
          <ResourceGrid>
            {[
              {
                title: 'U1 One-Pager',
                description:
                  'United One at a glance — the products, the plans and how it all fits together.',
                key: 'onePager'
              },
              {
                title: 'Compensation Plan',
                description:
                  'How partners are rewarded. Paid on product sales only.',
                key: 'compensationPlan'
              }
            ].map((document) => (
              <DocumentCard key={document.key}>
                <DocumentHeading>
                  <DocumentBadge>PDF</DocumentBadge>
                  <div>
                    <small>OFFICIAL DOCUMENT · PDF</small>
                    <h4>{document.title}</h4>
                  </div>
                </DocumentHeading>
                <DocumentDescription>
                  {document.description}
                </DocumentDescription>
                <ResourceAction {...externalProps(document.key)}>
                  Download PDF
                  <ActionIcon>
                    <Download />
                  </ActionIcon>
                </ResourceAction>
              </DocumentCard>
            ))}
            <DocumentCard $wide>
              <DocumentHeading>
                <DocumentBadge>
                  <DriveIcon />
                </DocumentBadge>
                <div>
                  <small>GOOGLE DRIVE · TRANSLATIONS</small>
                  <h4>Translated documents library</h4>
                </div>
              </DocumentHeading>
              <DocumentDescription>
                Every PDF, one-pager and tutorial translated into each available
                language. Files open in a shared Google Drive folder — view or
                download what you need.
              </DocumentDescription>
              <LanguageList>
                {[
                  'English',
                  'Español',
                  'Français',
                  'Deutsch',
                  'Hindi',
                  'Bangla',
                  'Română',
                  'Čeština',
                  '+ more'
                ].map((language) => (
                  <span key={language}>{language}</span>
                ))}
              </LanguageList>
              <PrimaryAction {...externalProps('translations')}>
                Open translations folder
                <Arrow />
              </PrimaryAction>
            </DocumentCard>
          </ResourceGrid>
        </ResourceSection>

        <ResourceSection>
          <H3 color={colors.neutral500}>CONNECT WITH US</H3>
          <P2 mb='28'>Follow, watch and join the community. Tap any logo.</P2>
          <ResourceGrid>
            {[
              {
                id: 'youtube',
                name: 'YouTube',
                description: 'Watch tutorials'
              },
              {
                id: 'instagram',
                name: 'Instagram',
                description: 'See our updates',
                icon: instagramIcon
              },
              {
                id: 'facebook',
                name: 'Facebook',
                description: 'Follow our page',
                icon: facebookIcon
              },
              {
                id: 'telegram',
                name: 'Telegram',
                description: 'Join community'
              }
            ].map((social) => (
              <SocialCard key={social.id} {...externalProps(social.id)}>
                <SocialIcon $platform={social.id}>
                  {social.icon ? (
                    <img src={social.icon} alt='' />
                  ) : social.id === 'youtube' ? (
                    <svg
                      width='32'
                      height='26'
                      viewBox='0 0 32 26'
                      aria-hidden='true'>
                      <rect
                        x='1'
                        y='3'
                        width='30'
                        height='21'
                        rx='6'
                        fill='white'
                      />
                      <path fill='red' d='m13 8 9 5-9 5z' />
                    </svg>
                  ) : (
                    <svg
                      width='32'
                      height='32'
                      viewBox='0 0 24 24'
                      aria-hidden='true'>
                      <path
                        fill='white'
                        d='M21.8 3.2 18.4 20c-.2 1-1 1.2-1.8.7l-5.2-3.9-2.5 2.4c-.3.3-.5.5-1 .5l.4-5.3L18 5.7c.4-.4-.1-.6-.6-.3L5.4 13l-5-1.6c-1-.3-1-1 .2-1.5L20 2.4c.9-.3 1.7.2 1.8.8z'
                      />
                    </svg>
                  )}
                </SocialIcon>
                <div>
                  <h4>{social.name}</h4>
                  <p>{social.description}</p>
                </div>
                <Arrow />
              </SocialCard>
            ))}
          </ResourceGrid>
        </ResourceSection>
      </ResourceLayout>
    </AnimatedDashboard>
  );
};
